import { useEffect, useMemo } from "react";
import { t } from "ttag";
import _ from "underscore";

import {
  skipToken,
  useListCollectionItemsQuery,
  useListCollectionsQuery,
  useListSnippetsQuery,
} from "metabase/api";
import { useDebouncedValue } from "metabase/common/hooks/use-debounced-value";
import { getIcon } from "metabase/lib/icon";
import { useMetadataToasts } from "metabase/metadata/hooks";
import type { Collection } from "metabase-types/api";

import type { TreeItem } from "./types";
import { buildSnippetTree } from "./utils";

export const useBuildTreeForCollection = (
  collection?: Collection,
): {
  isLoading: boolean;
  hasChildren?: boolean;
  tree: TreeItem[];
  error?: unknown;
} => {
  const {
    data: items,
    isLoading,
    error,
  } = useListCollectionItemsQuery(
    collection ? { id: collection.id, models: ["metric", "table"] } : skipToken,
  );

  return useMemo(() => {
    if (isLoading || !items || !collection) {
      return {
        isLoading,
        tree: [],
        error,
      };
    }
    return {
      isLoading,
      error,
      hasChildren: items.data.length > 0,
      tree: [
        {
          name: collection.name,
          id: collection.id,
          icon: getIcon({ ...collection, model: "collection" }).name,
          data: { ...collection, model: "collection" },
          model: "collection",
          children: items.data.map((item) => ({
            name: item.name,
            updatedAt: item["last-edit-info"]?.timestamp,
            icon: getIcon({ model: item.model }).name,
            data: item,
            id: item.id,
            model: item.model,
          })),
        },
      ],
    };
  }, [isLoading, items, collection, error]);
};

export const useBuildSnippetTree = (): {
  isLoading: boolean;
  tree: TreeItem[];
  hasChildren?: boolean;
  error?: unknown;
} => {
  const {
    data: snippets,
    isLoading: loadingSnippets,
    error,
  } = useListSnippetsQuery();
  const { data: snippetCollections, isLoading: loadingCollections } =
    useListCollectionsQuery({
      namespace: "snippets",
    });

  return useMemo(() => {
    if (
      loadingSnippets ||
      loadingCollections ||
      !snippets ||
      !snippetCollections
    ) {
      return {
        isLoading: true,
        tree: [],
        error,
      };
    }

    return {
      isLoading: false,
      error,
      tree: buildSnippetTree(snippetCollections, snippets),
      hasChildren: snippets.length > 0 || snippetCollections.length > 1,
    };
  }, [
    loadingSnippets,
    loadingCollections,
    snippets,
    snippetCollections,
    error,
  ]);
};

export const useErrorHandling = (_error: unknown) => {
  const error = useDebouncedValue(_error, 1000);
  const { sendErrorToast } = useMetadataToasts();

  useEffect(() => {
    if (_.isObject(error) && typeof error?.data?.message === "string") {
      sendErrorToast(
        t`Data couldn't be fetched properly: ${error.data.message}`,
      );
    }
  }, [error, sendErrorToast]);
};
